import { useState } from 'react'

interface RowKeyState {
	keys: number[]
	next: number
}

/**
 * Stable React keys for an editable list whose rows have no identity of their own (a link or
 * a code being typed changes on every keystroke). Keyed by position instead, removing a row
 * hands the next row's DOM, and its focus and caret, to the one that moved up.
 *
 * Call `add` and `remove` beside the matching change to the list. A list that grows without
 * them (a reset from outside) still gets a key for every row.
 */
export function useRowKeys(length: number) {
	const [state, setState] = useState<RowKeyState>(() => ({
		keys: Array.from({ length }, (_, i) => i),
		next: length,
	}))
	// Negative, so a fallback never collides with a key `add` hands out.
	const keys = Array.from({ length }, (_, i) => state.keys[i] ?? -(i + 1))
	return {
		keys,
		add: () => setState((s) => ({ keys: [...s.keys, s.next], next: s.next + 1 })),
		remove: (index: number) => setState((s) => ({ ...s, keys: s.keys.filter((_, i) => i !== index) })),
	}
}
